import {CustomEditor, DowncastWriter, EditorConfig} from 'ckeditor5-extra-build'
import {useCallback, useMemo, useRef} from 'react'
import {DataObjectId, Hub} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Sync} from '@client/sync'
import {getVariationMentionText} from '@components/common/smart-textedit/variation-mention-text/get-variation-mention-text'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {ChatUtils} from '@extensions/chat-utils'
import {UserUtils} from '@extensions/user-utils'
import {DepartmentsClient} from '../../../client/departments-client'
import {renderMentionListItem} from './render-mention-list-item'
import {MentionModelData, MentionUserInfo, userToMentionUserInfo} from './util'

// must be able to parse element downcasted by `initMentionDowncastConversion`
export const mentionElemRegEx =
  /<span\s[^>]*data-user="(?<userId>[^"]+)"[^>]*>(?<replacement>@[^<]+)<\/span>/

const getUserListByMention = (
  mentionText: string,
  hub?: Hub
): MentionUserInfo[] => {
  const box = Sync.getCurrentSandbox()
  let users = box.getAllUsers(false)

  const kind = ChatUtils.getChatKind(hub)

  if (kind === 'task') {
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    users = users.filter(
      u =>
        ChatSubscribers.isSubscribed(u.id, hub) ||
        ChatSubscribers.canBeSubscribed(u.id, hub, calculatedHierarchy)
    )
  }

  if (kind === 'group') {
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    users = users.filter(u =>
      ChatSubscribers.canBeSubscribed(u.id, hub, calculatedHierarchy)
    )
  }

  const variations = getVariationMentionText(mentionText)
  if (variations) {
    users = users.filter(u => {
      const userName = Accounts.getName(u.id).toLowerCase()
      return variations.some(text => userName.includes(text))
    })
  }

  users = UserUtils.sortByName(users)

  return users.map(x => userToMentionUserInfo(box, x))
}

/**
 * Convert a view (html element) to a model (internal representation)
 */
const initMentionUpcastConversion = (editor: CustomEditor): void => {
  editor.conversion.for('upcast').elementToAttribute({
    view: {
      name: 'span',
      attributes: {'data-user': true}
    },
    model: {
      key: 'mention',
      value: (viewItem: HTMLElement) => {
        const data: MentionModelData = {
          userId: (viewItem.getAttribute('data-user') ?? '') as DataObjectId
        }
        return (
          editor.plugins
            .get('Mention')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .toMentionAttribute(viewItem, data as any)
        )
      }
    },
    converterPriority: 'high'
  })
}

/**
 * Convert a model to a view
 */
const initMentionDowncastConversion = (editor: CustomEditor): void => {
  // `downcast` transforms model both to editor markup and to result markup (i.e. `getData()`)
  // see https://ckeditor.com/docs/ckeditor5/latest/framework/guides/deep-dive/conversion/downcast.html#downcast-pipelines
  editor.conversion.for('downcast').attributeToElement({
    model: 'mention',
    view: (
      value: MentionUserInfo | undefined,
      {writer}: {writer: DowncastWriter}
    ) => {
      // do not convert empty attributes (lack of value means no mention)
      if (!value) {
        return null
      }

      // must return an element parsable by `mentionElemRegEx`
      return writer.createAttributeElement(
        'span',
        {
          'data-user': value.userId ?? '',
          class: 'message-item-new__mention'
        },
        {
          priority: 20, // make mention attribute to be wrapped by other attribute elements
          id: value.userId // prevent merging mentions together
        }
      )
    },
    converterPriority: 'high'
  })
}

export const initMentionConversions = (editor: CustomEditor): void => {
  initMentionUpcastConversion(editor)
  initMentionDowncastConversion(editor)
}

export const useCKEditorCustomMentions = (
  mentionClass?: string,
  hub?: Hub
): EditorConfig => {
  const mentionRef = useRef<string>('')

  // CKEditor Mention plugin config has insufficient typing, so `any` is used

  const feed = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mentionText: string): any => {
      mentionRef.current = mentionText
      return getUserListByMention(mentionText, hub)
    },
    [mentionRef, hub]
  )

  const itemRenderer = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any): HTMLElement => {
      const vars = getVariationMentionText(mentionRef.current)
      return renderMentionListItem(item, vars, mentionClass)
    },
    [mentionRef, mentionClass]
  )

  return useMemo(
    () =>
      ({
        mention: {
          dropdownLimit: Infinity,
          feeds: [{marker: '@', feed, itemRenderer}]
        }
      }) as EditorConfig,
    [feed, itemRenderer]
  )
}
